import { appConfig } from '../../config/appConfig';

const getApiBaseUrl = (): string => {
  return appConfig.api.baseUrl || 'http://localhost:3001';
};

/**
 * Convert a blob storage URL to a proxied URL to avoid CORS issues
 * @param url The original blob storage URL
 * @returns A proxied URL that will work in the browser
 */
export const getProxiedImageUrl = (url: string | undefined): string | undefined => {
  if (!url) return undefined;
  
  try {
    // CRITICAL: Don't double-proxy! If it's already a proxy URL, return as-is
    if (url.includes('/api/image-proxy')) {
      return url;
    }
    
    // Check if it's a blob storage URL (starts with https:// and contains blob.core)
    if (url.startsWith('https://') && url.includes('.blob.core.windows.net')) {
      // Encode the URL and pass it through our proxy
      const encodedUrl = encodeURIComponent(url);
      return `${getApiBaseUrl()}/api/image-proxy?imageUrl=${encodedUrl}`;
    }
    
    // If it's not a blob storage URL, return it as is
    return url;
  } catch (error) {
    console.error('Error creating proxied URL:', error);
    return undefined;
  }
};

/**
 * Sanitize a URL with SAS token to ensure proper encoding
 * @param url The URL to sanitize
 * @returns A properly encoded URL
 */
export const sanitizeUrl = (url: string | undefined): string | undefined => {
  if (!url) return undefined;
  
  try {
    // For URLs with SAS tokens, we need to be careful with encoding
    // Just return the URL as-is, and let the proxy handle the storage account name
    return url;
  } catch (error) {
    console.error('Invalid URL:', url);
    return undefined;
  }
};

// Fallback image paths
const FALLBACK_IMAGE_BASE_PATH = '/assets/product-fallbacks';
const GENERIC_PRODUCT_FALLBACK = `${FALLBACK_IMAGE_BASE_PATH}/product-generic.png`;

// Mapping of categories to fallback images based on what's actually in the directory
const CATEGORY_FALLBACKS: Record<string, string> = {
  'Tops': `${FALLBACK_IMAGE_BASE_PATH}/shirt-fallback.png`,
  'Shirts': `${FALLBACK_IMAGE_BASE_PATH}/shirt-fallback.png`,
  'Bottoms': `${FALLBACK_IMAGE_BASE_PATH}/pants-fallback.png`,
  'Pants': `${FALLBACK_IMAGE_BASE_PATH}/pants-fallback.png`,
  'Outerwear': `${FALLBACK_IMAGE_BASE_PATH}/jacket-fallback.png`,
  'Jackets': `${FALLBACK_IMAGE_BASE_PATH}/jacket-fallback.png`,
  'Headwear': `${FALLBACK_IMAGE_BASE_PATH}/hat-fallback.png`,
  'Hats': `${FALLBACK_IMAGE_BASE_PATH}/hat-fallback.png`,
};

/**
 * Get a fallback image URL based on product category
 * @param category The product category
 * @returns URL to a fallback image
 */
export const getFallbackImageUrl = (category?: string): string => {
  if (category) {
    // Normalize the category name (convert underscores to spaces, capitalize words)
    const normalizedCategory = category
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
    
    // Return category-specific fallback if available
    if (CATEGORY_FALLBACKS[normalizedCategory]) {
      return CATEGORY_FALLBACKS[normalizedCategory];
    }
  }
  
  // Default fallback
  return GENERIC_PRODUCT_FALLBACK;
};

/**
 * Get a fallback color swatch image URL
 * @param colorName The color name
 * @param colorCode The color code
 * @returns URL to a fallback swatch image
 */
export const getColorSwatchFallback = (colorName?: string, colorCode?: string): string => {
  // For now, we use the generic product fallback for swatches
  return GENERIC_PRODUCT_FALLBACK;
};